import React, { useState, useEffect, useRef, useMemo } from "react";
import LocalFilterPanel from "../../../components/common/LocalFilterPanel";
import TooltipAttributeSettingsField from "../../../components/common/TooltipAttributeSettingsField";
import { makeRequest } from "../../../services/api/client";
import { useOcelMetadata } from "../../../stores/OcelContext";
import {
  SectionHeader,
  ActivityFilterBlock,
  ComparisonSection,
  BetrachtungszeitraumSection,
} from "../_shared/PeriodSettingsControls";

// ── Ampel color picker ──────────────────────────────────────────────────────
const PALETTE = [
  { hex: '#22c55e', label: 'Green' },
  { hex: '#eab308', label: 'Yellow' },
  { hex: '#ef4444', label: 'Red' },
  { hex: '#f97316', label: 'Orange' },
  { hex: '#6366f1', label: 'Default' },
];

const ColorPicker = ({ value, onChange, label, disabled }) => {
  const inputRef = useRef(null);
  const isCustom = !PALETTE.some((p) => p.hex === value);
  return (
    <div>
      {label && <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>}
      <div className="flex items-center gap-1.5 flex-wrap">
        {PALETTE.map(({ hex, label: l }) => (
          <button key={hex} onClick={() => onChange(hex)} title={l} type="button" disabled={disabled}
            className={`w-7 h-7 rounded-full transition-all cursor-pointer ${value === hex ? 'ring-2 ring-offset-2 ring-gray-600 scale-110' : 'opacity-75 hover:opacity-100 hover:scale-110'} disabled:opacity-40 disabled:cursor-not-allowed`}
            style={{ backgroundColor: hex }} />
        ))}
        {isCustom && (
          <button onClick={() => inputRef.current?.click()} title={value} type="button" disabled={disabled}
            className="w-7 h-7 rounded-full ring-2 ring-offset-2 ring-gray-600 scale-110 cursor-pointer"
            style={{ backgroundColor: value }} />
        )}
        <div className="relative w-7 h-7">
          <input ref={inputRef} type="color" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}
            className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" tabIndex={-1} />
          <div className="w-7 h-7 rounded-full border border-gray-300 bg-white flex items-center justify-center text-gray-400 pointer-events-none text-[10px]">
            ✎
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Formula row builder (used for both Zähler and Nenner) ──────────────────
const makeRowId = () => `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const FormulaSection = ({ title, rows, onChange, attributeOptions, attrsLoading, loading }) => {
  const handleAttributeChange = (id, attribute) => {
    onChange(rows.map((r) => (r.id === id ? { ...r, attribute } : r)));
  };

  const handleOperatorChange = (id, operator) => {
    onChange(rows.map((r) => (r.id === id ? { ...r, operator } : r)));
  };

  const handleAddRow = () => {
    onChange([...rows, { id: makeRowId(), attribute: "", operator: "+" }]);
  };

  const handleRemoveRow = (id) => {
    if (rows.length <= 1) return; // at least one row required
    onChange(rows.filter((r) => r.id !== id));
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{title}</label>
      <div className="space-y-2">
        {rows.map((row, index) => (
          <div key={row.id} className="flex items-center gap-2">
            <div className="w-14 flex-shrink-0">
              {index > 0 ? (
                <div className="flex gap-1">
                  {["+", "-"].map((op) => (
                    <button
                      key={op}
                      type="button"
                      onClick={() => handleOperatorChange(row.id, op)}
                      disabled={loading}
                      className={`flex-1 h-9 text-sm font-semibold rounded border transition-colors ${
                        row.operator === op
                          ? "bg-primary-600 text-white border-primary-600"
                          : "bg-white text-gray-600 border-gray-300 hover:border-primary-400"
                      }`}
                    >
                      {op}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="h-9" />
              )}
            </div>
            {attrsLoading ? (
              <p className="flex-1 text-xs text-gray-400">Loading attributes…</p>
            ) : (
              <select
                value={row.attribute}
                onChange={(e) => handleAttributeChange(row.id, e.target.value)}
                disabled={loading || attributeOptions.length === 0}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-gray-50 disabled:text-gray-400"
              >
                <option value="">Attribut auswählen…</option>
                {attributeOptions.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            )}
            <button
              type="button"
              onClick={() => handleRemoveRow(row.id)}
              disabled={loading || rows.length <= 1}
              title="Zeile entfernen"
              className="flex-shrink-0 w-8 h-9 flex items-center justify-center text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={handleAddRow}
        disabled={loading || attributeOptions.length === 0}
        className="mt-2 flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        + Attribut hinzufügen
      </button>
    </div>
  );
};

// ── Ampel (traffic light) configuration ─────────────────────────────────────
const AmpelSection = ({
  enabled, lower, upper, colorRed, colorYellow, colorGreen,
  onEnabledChange, onLowerChange, onUpperChange, onColorRedChange, onColorYellowChange, onColorGreenChange,
  loading,
}) => {
  const [lowerInput, setLowerInput] = useState(String(lower ?? 0));
  const [upperInput, setUpperInput] = useState(String(upper ?? 100));

  const lowerNum = parseFloat(lowerInput);
  const upperNum = parseFloat(upperInput);
  const rangeError = enabled && !isNaN(lowerNum) && !isNaN(upperNum) && lowerNum >= upperNum
    ? 'Unterer Schwellenwert muss kleiner als der obere sein.' : null;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <label className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors">
        <input type="checkbox" checked={enabled} onChange={(e) => onEnabledChange(e.target.checked)}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" disabled={loading} />
        <span className="text-xs font-semibold text-gray-700">Ampel aktivieren</span>
      </label>
      {enabled && (
        <div className="px-3 py-3 space-y-3 bg-white">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Unterer Schwellenwert (%)</label>
              <input type="number" step="any" value={lowerInput}
                onChange={(e) => { setLowerInput(e.target.value); const p = parseFloat(e.target.value); if (!isNaN(p)) onLowerChange(p); }}
                onBlur={() => { const p = parseFloat(lowerInput); setLowerInput(String(isNaN(p) ? 0 : p)); onLowerChange(isNaN(p) ? 0 : p); }}
                className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${rangeError ? 'border-red-400' : 'border-gray-300'}`} disabled={loading} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Oberer Schwellenwert (%)</label>
              <input type="number" step="any" value={upperInput}
                onChange={(e) => { setUpperInput(e.target.value); const p = parseFloat(e.target.value); if (!isNaN(p)) onUpperChange(p); }}
                onBlur={() => { const p = parseFloat(upperInput); setUpperInput(String(isNaN(p) ? 0 : p)); onUpperChange(isNaN(p) ? 0 : p); }}
                className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${rangeError ? 'border-red-400' : 'border-gray-300'}`} disabled={loading} />
            </div>
          </div>
          {rangeError && <p className="text-xs text-red-500">{rangeError}</p>}
          <div className="grid grid-cols-3 gap-3">
            <ColorPicker value={colorRed} onChange={onColorRedChange} label="Rot (< unterer)" disabled={loading} />
            <ColorPicker value={colorYellow} onChange={onColorYellowChange} label="Gelb (dazwischen)" disabled={loading} />
            <ColorPicker value={colorGreen} onChange={onColorGreenChange} label="Grün (>= oberer)" disabled={loading} />
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main settings component ─────────────────────────────────────────────────
const EfficiencyKPISettings = ({
  componentTitle,
  onTitleChange,
  objectType,
  onObjectTypeChange,
  numerator,
  onNumeratorChange,
  denominator,
  onDenominatorChange,
  tooltipAttributes,
  onTooltipAttributesChange,
  ampelEnabled,
  ampelLower,
  ampelUpper,
  ampelColorRed,
  ampelColorYellow,
  ampelColorGreen,
  onAmpelEnabledChange,
  onAmpelLowerChange,
  onAmpelUpperChange,
  onAmpelColorRedChange,
  onAmpelColorYellowChange,
  onAmpelColorGreenChange,
  availableObjectTypes,
  availableActivities,
  localFilters,
  updateLocalFilter,
  activeFile,
  loading,
  // Betrachtungszeitraum / Vorperioden-Vergleich
  periodPreset,
  onPeriodPresetChange,
  customStart,
  onCustomStartChange,
  customEnd,
  onCustomEndChange,
  showPeriodLabel,
  onShowPeriodLabelChange,
  comparisonEnabled,
  onComparisonEnabledChange,
  trendDirection,
  onTrendDirectionChange,
}) => {
  const metadata = useOcelMetadata(activeFile);
  const datasetMin = metadata?.date_range?.start ? metadata.date_range.start.split("T")[0] : undefined;
  const datasetMax = metadata?.date_range?.end ? metadata.date_range.end.split("T")[0] : undefined;

  const [scopedNumericAttributes, setScopedNumericAttributes] = useState(null);
  const [attrsLoading, setAttrsLoading] = useState(false);

  useEffect(() => {
    if (activeFile) {
      fetchAttributesForType(objectType || undefined);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAttributesForType = async (type) => {
    if (!activeFile) return;
    setAttrsLoading(true);
    try {
      const data = await makeRequest("/ocel/object-type-attributes", {
        params: { file_type: activeFile, object_type: type || undefined },
      }, "object type attributes");
      setScopedNumericAttributes(data?.numeric_attributes || []);
    } catch {
      setScopedNumericAttributes(null);
    } finally {
      setAttrsLoading(false);
    }
  };

  const attributeOptions = scopedNumericAttributes !== null
    ? scopedNumericAttributes
    : (metadata?.object_attribute_names || []);

  // Tooltip attribute options — scoped to the selected object type via the
  // OCEL schema (mirrors GanttChart's effectiveAttributes / AggregationKPI),
  // falling back to the flat all-types list when no object type is chosen yet.
  const allObjectAttributes = metadata?.object_attribute_names || [];
  const tooltipAttributeOptions = useMemo(() => {
    if (!objectType) return allObjectAttributes;
    const scoped = (metadata?.object_types_schema || []).find((t) => t.name === objectType);
    const names = (scoped?.attributes || []).map((a) => a.name);
    return names.length > 0 ? names : allObjectAttributes;
  }, [objectType, metadata, allObjectAttributes]);

  const handleObjectTypeChange = (val) => {
    onObjectTypeChange(val);
    updateLocalFilter("objectTypes", val ? [val] : []);
    onNumeratorChange([{ id: makeRowId(), attribute: "", operator: null }]);
    onDenominatorChange([{ id: makeRowId(), attribute: "", operator: null }]);
    onTooltipAttributesChange([]);
    setScopedNumericAttributes(null);
    fetchAttributesForType(val || undefined);
  };

  return (
    <div className="space-y-5">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Component Title <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <input
          type="text"
          value={componentTitle}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder="Effizienz-KPI"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Object Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Objekttyp</label>
        <select
          value={objectType}
          onChange={(e) => handleObjectTypeChange(e.target.value)}
          disabled={loading || availableObjectTypes.length === 0}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-gray-50 disabled:text-gray-400"
        >
          <option value="">Objekttyp wählen…</option>
          {availableObjectTypes.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">Zähler und Nenner zeigen nur Attribute dieses Objekttyps.</p>
      </div>

      {/* Formula */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-5">
        <FormulaSection
          title="Zähler"
          rows={numerator}
          onChange={onNumeratorChange}
          attributeOptions={attributeOptions}
          attrsLoading={attrsLoading}
          loading={loading || !objectType}
        />
        <FormulaSection
          title="Nenner"
          rows={denominator}
          onChange={onDenominatorChange}
          attributeOptions={attributeOptions}
          attrsLoading={attrsLoading}
          loading={loading || !objectType}
        />
      </div>

      {/* Tooltip-Konfiguration — object attributes shown as a table when hovering
          the main value; optional. Shared field, see TooltipAttributeSettingsField. */}
      <TooltipAttributeSettingsField
        attributeOptions={tooltipAttributeOptions}
        selectedAttributes={tooltipAttributes}
        onSelectedAttributesChange={onTooltipAttributesChange}
        trigger="hover"
        disabled={loading}
      />

      {/* Betrachtungszeitraum */}
      <BetrachtungszeitraumSection
        periodPreset={periodPreset}
        onPeriodPresetChange={onPeriodPresetChange}
        customStart={customStart}
        onCustomStartChange={onCustomStartChange}
        customEnd={customEnd}
        onCustomEndChange={onCustomEndChange}
        datasetMin={datasetMin}
        datasetMax={datasetMax}
        comparisonEnabled={comparisonEnabled}
      />

      {/* Periodenbezeichnung anzeigen */}
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={showPeriodLabel}
          onChange={(e) => onShowPeriodLabelChange(e.target.checked)}
          disabled={loading}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="text-sm text-gray-700">Periodenbezeichnung anzeigen</span>
      </label>

      {/* Ampel — unabhängig vom Vorperioden-Vergleich */}
      <AmpelSection
        enabled={ampelEnabled}
        lower={ampelLower}
        upper={ampelUpper}
        colorRed={ampelColorRed}
        colorYellow={ampelColorYellow}
        colorGreen={ampelColorGreen}
        onEnabledChange={onAmpelEnabledChange}
        onLowerChange={onAmpelLowerChange}
        onUpperChange={onAmpelUpperChange}
        onColorRedChange={onAmpelColorRedChange}
        onColorYellowChange={onAmpelColorYellowChange}
        onColorGreenChange={onAmpelColorGreenChange}
        loading={loading}
      />

      {/* Vorperioden-Vergleich — unabhängig von der Ampel */}
      <ComparisonSection
        periodPreset={periodPreset}
        comparisonEnabled={comparisonEnabled}
        onComparisonEnabledChange={onComparisonEnabledChange}
        trendDirection={trendDirection}
        onTrendDirectionChange={onTrendDirectionChange}
        loading={loading}
        radioGroupName="efficiencyKpiTrendDirection"
      />

      {/* Activity Filter — within time scope */}
      <div>
        <SectionHeader
          title="Aktivitäten im Betrachtungszeitraum"
          subtitle="Nur Objekte, die die gewählten Aktivitäten innerhalb des oben gewählten Zeitraums ausgeführt haben."
        />
        <div className="mt-3">
          <ActivityFilterBlock
            filterKey="activityFilters"
            localFilters={localFilters}
            updateLocalFilter={updateLocalFilter}
            availableActivities={availableActivities}
            loading={loading}
          />
        </div>
      </div>

      {/* Activity Filter — full lifecycle */}
      <div>
        <SectionHeader
          title="Aktivitäten im Lebenszyklus"
          subtitle="Nur Objekte, die diese Aktivitäten jemals in ihrer gesamten Geschichte hatten — unabhängig vom Betrachtungszeitraum."
        />
        <div className="mt-3">
          <ActivityFilterBlock
            filterKey="lifecycleActivityFilters"
            localFilters={localFilters}
            updateLocalFilter={updateLocalFilter}
            availableActivities={availableActivities}
            loading={loading}
          />
        </div>
      </div>

      {/* Standard Local Filters — dateRange deckt der Betrachtungszeitraum oben bereits ab,
          activityNodes wurde zugunsten der beiden ActivityFilterBlock-Abschnitte oben entfernt
          (Rücksprache: bewusster Breaking Change für evtl. aktive alte activityNodes-Configs). */}
      <LocalFilterPanel
        localFilters={localFilters}
        updateLocalFilter={updateLocalFilter}
        activeFile={activeFile}
        disabled={loading}
        enabledFilterTypes={['objectAttributes']}
      />
    </div>
  );
};

export default EfficiencyKPISettings;
export { makeRowId };
